import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';

import {
    addToCart,
    clearCart,
    removeCartItem,
    updateCartItem
} from '../../store/reducers/cartReducer';

import { create as createOrder } from '../../apis/orders';
import { useShowNotification } from '../../hooks/useShowNotification';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useModal } from '../../hooks/useModal';

import { CartBreadcrumb } from '../../components/shop/CartBreadcrumb';
import { CartItemsList } from '../../components/shop/CartItemsList';
import { CartEmptyIcon } from '../../components/shop/CartEmptyIcon';
import { SearchModal } from '../../components/search/SearchModal';
import { EmptyCard } from '../../components/common/ui/EmptyCard';
import { CartSummary } from '../../components/shop/CartSummary';

export const CartPage = () => {
    useDocumentTitle('Shopping Cart');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const showNotification = useShowNotification('Cart', 'shopping-cart');

    const {
        showModal: showSearchModal,
        handleShow: handleShowSearchModal,
        handleHide: handleHideSearchModal
    } = useModal(false);

    const user = useSelector((state) => state.auth.user);
    const { items } = useSelector((state) => state.cart);

    const handleAddItem = (item) => {
        dispatch(addToCart(item));
        showNotification(`${item.product.name} added`, 'primary');
    };

    const handleItemChange = (item) => {
        dispatch(updateCartItem(item));
        showNotification(`${item.product.name} quantity updated`, 'warning');
    };

    const handleItemRemove = (item) => {
        dispatch(removeCartItem(item));
        showNotification(`${item.product.name} removed`, 'danger');
    };

    const handleCompleteOrder = async () => {
        try {
            if (user == null) {
                navigate('/login');
                return;
            }

            const orderItems = items.map((item) => ({
                ...item,
                price: item.product.price,
                product: item.product.id
            }));

            const response = await createOrder({
                items: orderItems
            });

            if (response.success) {
                showNotification(response.message, 'success');
                navigate('/user/orders');
                dispatch(clearCart());
                return;
            }

            showNotification(response.message, 'warning');
        } catch (error) {
            showNotification('Oops something went wrong.', 'danger');
        }
    };

    const handleClearOrder = () => {
        dispatch(clearCart());
        showNotification('Cart items cleared', 'danger');
    };

    return (
        <div className="cart-page mb-5">
            <h1 className="h3">Shopping cart</h1>
            <CartBreadcrumb />
            {items.length > 0 ? (
                <div className="row">
                    <div className="col-md-6 col-lg-8 col-xl-9">
                        <CartItemsList
                            items={items}
                            className="mt-3 mb-3"
                            onItemChange={handleItemChange}
                            onItemRemove={handleItemRemove}
                        />
                    </div>
                    <div className="col-md-6 col-lg-4 col-xl-3">
                        <CartSummary
                            items={items}
                            onCompleteOrder={handleCompleteOrder}
                            onSearchGifts={handleShowSearchModal}
                            onClearOrder={handleClearOrder}
                        />
                        <SearchModal
                            show={showSearchModal}
                            onHide={handleHideSearchModal}
                            onAddItem={handleAddItem}
                            addItemText="Add to cart"
                        />
                    </div>
                </div>
            ) : (
                <EmptyCard emptyIcon={CartEmptyIcon} text="Your cart is empty">
                    <NavLink to="/search" className="btn btn-outline-primary">
                        Search gifts
                    </NavLink>
                </EmptyCard>
            )}
        </div>
    );
};
